import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import CaptureTipDrawer from "@components/drawer/CaptureTipDrawer";
import { Flex } from "antd";
import { useInfiniteQuery } from "@tanstack/react-query";
import useIntersection from "@hook/useIntersection";
import {
  GET_PRODUCT_INVENTORY_URL,
} from "@libs/apiUrl";
import useAxios from "@libs/axios/useAxios";

const CaptureList = () => {
  const theme = useTheme();
  const [openTip, setOpenTip] = useState(false);
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { axiosInstance } = useAxios();
  const [searchParams] = useSearchParams();

  const categoryPathsStr = searchParams.get("category_paths") || "";

  const categoryPaths = categoryPathsStr
    ? decodeURIComponent(categoryPathsStr)
        .split("|")
        .map((str) => str.trim())
    : [];

  const filters = useMemo(
    () => ({
      product_name: searchParams.get("product_name") ?? "",
      status: searchParams.get("status") ?? "",
      stock_from: searchParams.get("stock_from") ?? "",
      stock_to: searchParams.get("stock_to") ?? "",
      category: searchParams.get("category") ?? "",
      status_labels: searchParams.get("status_labels") ?? "",
    }),
    [searchParams],
  );

  // 상품 등록 내역 간소화 조회
  const getProductInventorySimple = useCallback(
    async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam - 1,
        size: 10,
        product_name: filters.product_name,
        status: filters.status,
        stock_from: filters.stock_from,
        stock_to: filters.stock_to,
        category: filters.category,
      };

      const searchParams = new URLSearchParams(
        Object.entries(params)
          .filter(([_, value]) => value !== "")
          .map(([key, value]) => [key, String(value)]),
      );

      const res = await axiosInstance.get(
        `${GET_PRODUCT_INVENTORY_URL}?${searchParams}`,
      );
      return res.status === 200 ? res.data : null;
    },
    [axiosInstance, filters],
  );

  const {
    data: productInventorySimple,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["getProductInventorySimple", filters],
    queryFn: getProductInventorySimple,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.data?.total_count || 0;
      const loadedCount = allPages.reduce(
        (sum, page) => sum + (page?.data?.list?.length || 0),
        0,
      );

      // 다음 페이지가 있는지 판단
      return loadedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  const handleOpenTip = () => {
    setOpenTip(true);
  };

  const handleCameraClick = () => {
    const hasSeenGuide = localStorage.getItem("guideSeen");

    if (!hasSeenGuide) {
      localStorage.setItem("guideSeen", "true");
      navigate("/capture-popup");
    } else {
      cameraInputRef.current?.click();
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      navigate("/capture", {
        state: {
          image: url, // 화면 표시용
          imageFile: file, // API 업로드용 (파일)
        },
      });
    }
  };

  // draft_status → 프론트 status 코드 변환
  const mapDraftStatusToStatus = (draftStatus: string): string => {
    switch (draftStatus) {
      case "draft":
        return "STOCK_TEMP";
      case "confirmed":
        return "STOCK";
      case "sale_draft":
        return "SALE_TEMP";
      case "sale_confirmed":
        return "SALE";
      default:
        return draftStatus;
    }
  };

  const handleClick = (item: any) => {
    navigate(`/product-detail/${item.product_id}/${mapDraftStatusToStatus(item.draft_status)}`);
  };

  // 필터값 여부
  const { ...filterWithoutOrderBy } = filters;
  const hasFilterValue = Object.values(filterWithoutOrderBy).some(
    (value) => value !== "",
  );
  const captureItems = useMemo(() => {
    const seen = new Set<string>();

    return (productInventorySimple?.pages || [])
      .flatMap((page) => page?.data?.list ?? [])
      .filter((item: any) => item != null)
      .filter((item: any) => {
        const key = `${item.product_id ?? "unknown"}-${item.draft_status ?? "unknown"}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [productInventorySimple?.pages]);

  return (
    <Container>
      <InputWrapper>
        <IconBox
          onClick={() => navigate("/search", { state: { from: "capture" } })}>
          <Image
            src={theme.image.search_icon.default}
            width="2.5rem"
            height="2.5rem"
          />
          <Name>
            {filters.product_name.length < 1 &&
              filters.status.length < 1 &&
              filters.stock_from.length < 1 &&
              filters.stock_to.length < 1 &&
              categoryPaths.length < 1 && <span>상품 검색</span>}
            {filters.product_name && filters.product_name !== ""
              ? `${filters.product_name},`
              : ""}
            {filters.status_labels && filters.status_labels !== ""
              ? ` ${filters.status_labels},`
              : ""}

            {filters.stock_to || filters.stock_from !== ""
              ? `${filters.stock_from} ~ ${filters.stock_to},`
              : ""}
            {categoryPaths.length > 0 ? `${categoryPaths.join(" , ")},` : ""}
          </Name>
        </IconBox>

        {hasFilterValue && (
          <DeleteButton
            onClick={() => navigate("/capture-list", { replace: true })}>
            <Image
              src={theme.image.close.default}
              height="2.4rem"
              width="2.4rem"
            />
          </DeleteButton>
        )}
      </InputWrapper>

      <TitleWrapper>
        <CaptureGuideButton onClick={handleOpenTip}>
          <TotalProduct>
            총 {productInventorySimple?.pages?.[0]?.data?.total_count}건
          </TotalProduct>

          <Flex style={{ alignItems: "center", gap: "0.5rem" }}>
            <button
              style={{
                paddingTop: "0.2rem",
              }}>
              <Image
                src={theme.image.warning_circle.default}
                width="1.6rem"
                height="1.6rem"
              />
            </button>

            <span>촬영가이드</span>
          </Flex>
        </CaptureGuideButton>
      </TitleWrapper>

      <CaptureProductImage>
        <ImageWrapper>
          <Image
            src={theme.image.add_icon.default}
            width="10rem"
            height="10rem"
            onClick={handleCameraClick}
          />

          {captureItems.map((item: any, index: number) => (
              <ImageBox
                key={`${item.product_id ?? "unknown"}-${item.draft_status ?? "unknown"}-${index}`}
                onClick={() => handleClick(item)}
              >
                <Image
                  src={item.thumbnail_url}
                  alt="product"
                  width="10rem"
                  height="10rem"
                  radius="10px"
                />

                {/* 상태별 아이콘 */}
                <ImageIcon>
                  {item.draft_status === "sale_confirmed" && ( // 상품등록완료
                    <img
                      src={theme.image.check_success_icon.default}
                      alt="done"
                    />
                  )}
                </ImageIcon>

                <ImageIcon>
                  {(item.draft_status === "draft" ||
                    item.draft_status === "sale_draft") && ( // 임시저장
                    <Image
                      src={theme.image.danger_circle.default}
                      alt="draft_product"
                    />
                  )}
                </ImageIcon>
              </ImageBox>
            ))}
        </ImageWrapper>
      </CaptureProductImage>

      {/* 카메라 촬영용 */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageSelect}
      />

      <CaptureTipDrawer open={openTip} setOpen={setOpenTip} />

      {hasNextPage && <div ref={setObserverTarget} />}
    </Container>
  );
};

export default CaptureList;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  padding: "2rem",
  paddingTop: "0",
  minWidth: "375px",
});

const InputWrapper = styled.div(({ theme }) => ({
  width: "100%",
  height: "6rem",
  padding: "1rem 2rem 1rem 2rem",
  fontSize: "1.6rem",
  borderRadius: "10px",
  lineHeight: "1.6rem",
  fontWeight: theme.fontWeight.medium,
  backgroundColor: theme.color.border_gray,
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  position: "relative",
  cursor: "pointer",
  color: theme.color.gray,
  marginTop: "1rem",
}));

const DeleteButton = styled.button({
  position: "absolute",
  right: "2rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  cursor: "pointer",
});

const IconBox = styled.div({
  position: "absolute",
  top: "50%",
  left: "2rem",
  transform: "translate(0,-50%)",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  cursor: "pointer",
  overflow: "hidden",
  width: "100%",
});

const TitleWrapper = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "1rem 0",
}));

const CaptureGuideButton = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: "0.2rem",
  color: theme.color.dark_gray,
  cursor: "pointer",
}));

const TotalProduct = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
  fontWeight: theme.fontWeight.medium,
}));

const CaptureProductImage = styled.div(({ theme }) => ({
  padding: "2rem 0",
}));

const Name = styled.div(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
  paddingRight: "6rem", // DeleteButton 버튼 너비 + 여유 공간 확보
  fontSize: "1.6rem",
}));

const ImageWrapper = styled.div(() => ({
  display: "grid",
  // gridTemplateColumns: "repeat(3, 1fr)",
  gap: "1rem",
  gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
}));

const ImageBox = styled.div(({ theme }) => ({
  position: "relative",
  width: "10rem",
  height: "10rem",
  borderRadius: "10px",
}));

const ImageIcon = styled.div(({ theme }) => ({
  position: "absolute",
  top: "1rem",
  right: "1rem",
}));
